// star-ratings-retention.js - Tier 1 Educational
export const content = {
  heroStyle: 'default',
  tag: 'Plan Intelligence',

  html: `
<p>Medicare Star ratings are everywhere—on plan marketing, in carrier presentations, on Medicare.gov. But most agents treat them as background noise rather than the retention intelligence they actually are.</p>

<p>Star ratings affect everything from client satisfaction to bonus payments to SEP eligibility. Understanding how they impact your clients—and your retention—gives you an edge most agents don't have.</p>

<h2>What Star Ratings Actually Measure</h2>

<p>CMS rates Medicare Advantage and Part D plans on a 1-5 star scale based on quality measures in several categories:</p>

<h3>For MA Plans:</h3>
<ul>
<li>Staying healthy (screenings, tests, vaccines)</li>
<li>Managing chronic conditions</li>
<li>Member experience (satisfaction, complaints)</li>
<li>Customer service</li>
<li>Health plan responsiveness</li>
</ul>

<h3>For Part D:</h3>
<ul>
<li>Drug plan customer service</li>
<li>Member complaints</li>
<li>Member experience</li>
<li>Drug pricing and safety</li>
</ul>

<p>Plans receive an overall Star rating that combines these measures. The rating is updated annually based on the previous year's performance.</p>

<h2>Why Star Ratings Matter for Retention</h2>

<h3>1. Client Satisfaction Correlation</h3>

<p>Star ratings partly reflect member satisfaction. Plans with declining Stars often have underlying service or quality issues that your clients are experiencing—even if they haven't complained to you yet.</p>

<p>When a plan drops from 4 stars to 3 stars, that's not abstract. It means something measurable got worse: maybe customer service response times increased, or more claims were denied, or members reported worse experiences.</p>

<p>Your clients in that plan may be feeling those problems. The Star rating is a signal to check in.</p>

<h3>2. SEP Eligibility Trigger</h3>

<p>Here's something many agents miss: <strong>beneficiaries enrolled in plans with fewer than 3 stars have a Special Enrollment Period to switch to a higher-rated plan anytime during the year.</strong></p>

<p>This means:</p>
<ul>
<li>Your clients in low-Star plans can be recruited year-round by competitors</li>
<li>They have ongoing flexibility to leave—not just during AEP</li>
<li>Other agents know this and target low-Star plan members specifically</li>
</ul>

<p>If your book includes clients in sub-3-star plans, they're in constant shopping mode whether they realize it or not.</p>

<h3>3. Bonus Payment Impact</h3>

<p>Plans with 4+ stars receive quality bonus payments from CMS. These bonuses often fund the extra benefits that make plans attractive—OTC allowances, dental, fitness, meals.</p>

<p>When a plan loses its 4+ star status:</p>
<ul>
<li>Bonus payments may decrease</li>
<li>Extra benefits may be reduced</li>
<li>The plan becomes less competitive</li>
<li>Clients notice the changes and start shopping</li>
</ul>

<h2>Tracking Star Rating Changes</h2>

<p>Star ratings are released annually, typically in October (affecting the following plan year). Key things to track:</p>

<h3>For Each Plan in Your Book:</h3>
<ul>
<li>Current Star rating</li>
<li>Previous year's rating (trend direction)</li>
<li>Whether rating is above or below 3 stars (SEP threshold)</li>
<li>Whether rating is above or below 4 stars (bonus threshold)</li>
</ul>

<h3>Warning Signs:</h3>
<ul>
<li><strong>Drop below 4 stars:</strong> Plan may lose bonus funding, benefits may change</li>
<li><strong>Drop below 3 stars:</strong> Members gain SEP, become year-round churn risks</li>
<li><strong>Consistent decline:</strong> Plan trending wrong direction year-over-year</li>
<li><strong>Member experience scores dropping:</strong> Leading indicator of satisfaction problems</li>
</ul>

<h2>Star Rating Retention Strategies</h2>

<h3>When a Plan's Rating Drops</h3>

<p>If a plan in your book drops Stars, especially below key thresholds:</p>

<ol>
<li><strong>Identify affected clients</strong> – Who's enrolled in this plan?</li>
<li><strong>Research the specifics</strong> – What caused the drop? Which measures declined?</li>
<li><strong>Proactive outreach</strong> – "Your plan's Star rating changed. Here's what that means for you..."</li>
<li><strong>Address likely pain points</strong> – If customer service scores dropped, clients may be frustrated with the plan</li>
<li><strong>Discuss options</strong> – Should they stay? Switch? What are alternatives?</li>
</ol>

<h3>When Clients Are in Low-Star Plans</h3>

<p>For clients in sub-3-star plans:</p>
<ul>
<li>Acknowledge they have year-round flexibility to change</li>
<li>Proactively discuss whether the plan still serves them well</li>
<li>If appropriate, help them switch to a better-rated plan—with you, not another agent</li>
<li>If they're staying, explain why (maybe the network fit is perfect despite lower rating)</li>
</ul>

<p>Don't ignore the elephant in the room. Other agents are targeting your low-Star plan clients. Be the one who addresses it first.</p>

<h3>When Enrolling New Clients</h3>

<p>Star ratings should factor into enrollment decisions:</p>
<ul>
<li>Prefer 4+ star plans when appropriate for the client's needs</li>
<li>Be cautious about plans with declining trends</li>
<li>If enrolling in a lower-rated plan, explain why it's still the right choice</li>
<li>Document your reasoning in case the client questions it later</li>
</ul>

<h2>Communicating About Star Ratings</h2>

<p>Clients don't always understand Star ratings. Help them:</p>

<p><strong>What to explain:</strong></p>
<ul>
<li>"Star ratings are like grades for how well a plan performs"</li>
<li>"They measure things like customer service, how well the plan helps with health, and member satisfaction"</li>
<li>"Higher stars generally mean better experience, but it's not the only factor"</li>
<li>"Plans below 3 stars give you extra flexibility to switch if needed"</li>
</ul>

<p><strong>When to mention ratings:</strong></p>
<ul>
<li>At enrollment (especially if choosing between plans)</li>
<li>When ratings change significantly</li>
<li>When a client is in a low-rated plan</li>
<li>When a client asks about quality differences</li>
</ul>

<h2>The Bigger Picture</h2>

<p>Star ratings are one signal among many. A 3-star plan might be perfect for a client whose doctors are all in-network. A 5-star plan might be wrong if it doesn't cover their medications well.</p>

<p>Use Star ratings as intelligence, not absolute guidance:</p>
<ul>
<li>They tell you where to look for problems</li>
<li>They indicate when clients might be more vulnerable to switching</li>
<li>They help you anticipate plan changes before they affect clients</li>
<li>They're a conversation starter, not a conversation ender</li>
</ul>

<p>The agents who use Star ratings well don't just know the numbers—they know what the numbers mean for their specific clients and act on that knowledge proactively.</p>
  `,

  faqs: [
    {
      question: "What happens when a Medicare plan drops below 3 stars?",
      answer: "Members enrolled in plans with fewer than 3 stars have a Special Enrollment Period allowing them to switch to a higher-rated plan anytime during the year—not just AEP. This makes them year-round churn risks and targets for competitor recruiting."
    },
    {
      question: "How do Star ratings affect plan benefits?",
      answer: "Plans with 4+ stars receive quality bonus payments from CMS that often fund extra benefits (OTC allowances, dental, fitness). When a plan loses 4+ star status, it may lose bonus funding and reduce benefits, making the plan less competitive."
    },
    {
      question: "When are Star ratings released?",
      answer: "CMS releases Star ratings annually, typically in October, affecting the following plan year. This timing aligns with AEP, so rating changes are fresh when clients are making enrollment decisions."
    },
    {
      question: "Should I only enroll clients in 4+ star plans?",
      answer: "Not necessarily. Star ratings are one factor among many. A lower-rated plan might still be the right choice if it has the right network, formulary, or benefits for that client. Use ratings as intelligence, but prioritize individual client fit."
    },
    {
      question: "What should I do when a client's plan Star rating drops?",
      answer: "Proactively reach out to explain what changed and what it means for them. Research which measures declined. Discuss whether they should stay or explore alternatives. Being proactive prevents them from hearing about it from a competitor first."
    }
  ],

  sources: [
    {
      title: "Medicare Star Ratings",
      url: "https://www.cms.gov/Medicare/Prescription-Drug-Coverage/PrescriptionDrugCovGenIn/PerformanceData",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    },
    {
      title: "Special Enrollment Period for Low-Star Plans",
      url: "https://www.medicare.gov/sign-up-change-plans/when-can-i-join-a-health-or-drug-plan/special-circumstances",
      publisher: "Medicare.gov",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Star rating changes directly affect your retention risk. Pro IRP tracks Star ratings for every plan in your book and alerts you when ratings change, so you can act before clients become vulnerable to competitors.`
};

export default content;
